import { useState, useMemo, useEffect } from 'react';
import { X, Filter, Search, Tag, Database, Eye, EyeOff, RotateCcw, Check, Layers, Columns, Circle, Workflow, GitBranch } from 'lucide-react';
import useFlowStore from '../../stores/useFlowStore';

const FilterEntitiesModal = ({ isOpen, onClose }) => {
  const {
    nodes,
    diagramFilter,
    setDiagramFilter,
    clearDiagramFilter,
    setSelectedNodes,
    globalTags,
    globalDomains
  } = useFlowStore();

  const [localFilter, setLocalFilter] = useState(() => ({
    searchQuery: diagramFilter?.searchQuery || '',
    entityTypes: diagramFilter?.entityTypes || [],
    tags: diagramFilter?.tags || [],
    domain: diagramFilter?.domain || '',
    schema: diagramFilter?.schema || '',
    hideUnmatched: diagramFilter?.hideUnmatched || false
  }));

  // Gather entity type counts from current model
  const entityTypeCounts = useMemo(() => {
    const counts = {
      table: 0,
      view: 0,
      collection: 0,
      fact: 0,
      dimension: 0,
      text: 0
    };
    nodes.forEach(n => {
      if (counts[n.type] !== undefined) {
        counts[n.type]++;
      }
    });
    return counts;
  }, [nodes]);

  // Gather unique tags present in current nodes
  const availableTags = useMemo(() => {
    const tagSet = new Set();
    nodes.forEach(n => {
      (n.data?.tags || []).forEach(t => {
        const val = typeof t === 'string' ? t : t.text || t.name;
        if (val) tagSet.add(val);
      });
    });
    (globalTags || []).forEach(gt => {
      const val = typeof gt === 'string' ? gt : gt.name || gt.text;
      if (val) tagSet.add(val);
    });
    return Array.from(tagSet).sort();
  }, [nodes, globalTags]);

  // Gather unique domains present in current nodes
  const availableDomains = useMemo(() => {
    const domainSet = new Set();
    nodes.forEach(n => {
      (n.data?.domains || []).forEach(d => {
        const val = typeof d === 'string' ? d : d.text || d.name;
        if (val) domainSet.add(val);
      });
    });
    (globalDomains || []).forEach(gd => {
      const val = typeof gd === 'string' ? gd : gd.name || gd.text;
      if (val) domainSet.add(val);
    });
    return Array.from(domainSet).sort();
  }, [nodes, globalDomains]);

  // Gather unique schemas present in current nodes
  const availableSchemas = useMemo(() => {
    const schemaSet = new Set();
    nodes.forEach(n => {
      if (n.data?.schema) {
        schemaSet.add(n.data.schema);
      }
    });
    return Array.from(schemaSet).sort();
  }, [nodes]);

  // Check matching nodes count based on local filter
  const matchingNodes = useMemo(() => {
    return nodes.filter(node => {
      // Type filter
      if (localFilter.entityTypes.length > 0 && !localFilter.entityTypes.includes(node.type)) {
        return false;
      }
      // Text search
      if (localFilter.searchQuery.trim()) {
        const q = localFilter.searchQuery.trim().toLowerCase();
        const nameMatch = node.data?.name?.toLowerCase().includes(q);
        const schemaMatch = node.data?.schema?.toLowerCase().includes(q);
        const descMatch = node.data?.description?.toLowerCase().includes(q);
        const fieldMatch = (node.data?.fields || []).some(f =>
          f.name?.toLowerCase().includes(q) || f.type?.toLowerCase().includes(q)
        );
        if (!nameMatch && !schemaMatch && !descMatch && !fieldMatch) return false;
      }
      // Tags
      if (localFilter.tags.length > 0) {
        const nodeTags = (node.data?.tags || []).map(t => (typeof t === 'string' ? t : t.text || t.name).toLowerCase());
        const match = localFilter.tags.some(tag => nodeTags.includes(tag.toLowerCase()));
        if (!match) return false;
      }
      // Domain
      if (localFilter.domain.trim()) {
        const dTarget = localFilter.domain.trim().toLowerCase();
        const nodeDomains = (node.data?.domains || []).map(d => (typeof d === 'string' ? d : d.text || d.name).toLowerCase());
        const match = nodeDomains.some(dom => dom.includes(dTarget));
        if (!match) return false;
      }
      // Schema
      if (localFilter.schema.trim()) {
        const sTarget = localFilter.schema.trim().toLowerCase();
        const nodeSchema = (node.data?.schema || '').toLowerCase();
        if (!nodeSchema.includes(sTarget)) return false;
      }
      return true;
    });
  }, [nodes, localFilter]);

  const toggleEntityType = (type) => {
    setLocalFilter(prev => {
      const exists = prev.entityTypes.includes(type);
      const nextTypes = exists
        ? prev.entityTypes.filter(t => t !== type)
        : [...prev.entityTypes, type];
      return { ...prev, entityTypes: nextTypes };
    });
  };

  const toggleTag = (tag) => {
    setLocalFilter(prev => {
      const exists = prev.tags.includes(tag);
      const nextTags = exists
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag];
      return { ...prev, tags: nextTags };
    });
  };

  const handleApply = () => {
    setDiagramFilter(localFilter);
    onClose();
  };

  const handleReset = () => {
    clearDiagramFilter();
    setLocalFilter({
      searchQuery: '',
      entityTypes: [],
      tags: [],
      domain: '',
      schema: '',
      hideUnmatched: false
    });
  };

  const handleSelectMatchingOnCanvas = () => {
    setDiagramFilter(localFilter);
    if (matchingNodes.length > 0 && setSelectedNodes) {
      setSelectedNodes(matchingNodes);
    }
    onClose();
  };

  const hasActiveFilters =
    !!localFilter.searchQuery.trim() ||
    localFilter.entityTypes.length > 0 ||
    localFilter.tags.length > 0 ||
    !!localFilter.domain.trim() ||
    !!localFilter.schema.trim();

  useEffect(() => {
    if (isOpen) {
      setLocalFilter({
        searchQuery: diagramFilter?.searchQuery || '',
        entityTypes: diagramFilter?.entityTypes || [],
        tags: diagramFilter?.tags || [],
        domain: diagramFilter?.domain || '',
        schema: diagramFilter?.schema || '',
        hideUnmatched: diagramFilter?.hideUnmatched || false
      });
    }
  }, [isOpen, diagramFilter]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Filter size={22} className="text-blue-600 dark:text-blue-400" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Filter Diagram Entities
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Filter displayed objects by type, name, attributes, tags, or domain
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-200"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {/* Text Search */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-1.5">
              Search by Name or Field / Attribute
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Type entity name, field name, data type..."
                value={localFilter.searchQuery}
                onChange={(e) => setLocalFilter({ ...localFilter, searchQuery: e.target.value })}
                className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
              {localFilter.searchQuery && (
                <button
                  type="button"
                  onClick={() => setLocalFilter({ ...localFilter, searchQuery: '' })}
                  className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Entity Types Filter Pills */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-1.5">
              Entity Type
            </label>
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'table', label: 'Tables', count: entityTypeCounts.table },
                { id: 'view', label: 'Views', count: entityTypeCounts.view },
                { id: 'collection', label: 'Collections', count: entityTypeCounts.collection },
                { id: 'fact', label: 'Facts', count: entityTypeCounts.fact },
                { id: 'dimension', label: 'Dimensions', count: entityTypeCounts.dimension },
                { id: 'text', label: 'Text Notes', count: entityTypeCounts.text },
              ].map((t) => {
                const selected = localFilter.entityTypes.includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => toggleEntityType(t.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 border ${
                      selected
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {selected && <Check size={12} />}
                    <span>{t.label}</span>
                    <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${selected ? 'bg-blue-700 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'}`}>
                      {t.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tags Filter */}
          {availableTags.length > 0 && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-1.5">
                Filter by Tag
              </label>
              <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto p-1.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-750">
                {availableTags.map((tag) => {
                  const selected = localFilter.tags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleTag(tag)}
                      className={`px-2.5 py-1 rounded-full text-xs transition-colors flex items-center gap-1 border ${
                        selected
                          ? 'bg-purple-600 text-white border-purple-600 font-medium'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                      }`}
                    >
                      <Tag size={10} />
                      <span>{tag}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Domain Filter */}
          {availableDomains.length > 0 && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-1.5">
                Filter by Domain Classification
              </label>
              <select
                value={localFilter.domain}
                onChange={(e) => setLocalFilter({ ...localFilter, domain: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Domains</option>
                {availableDomains.map((dom) => (
                  <option key={dom} value={dom}>
                    Domain: {dom}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Schema Filter */}
          {availableSchemas.length > 0 && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-1.5">
                Filter by Schema
              </label>
              <select
                value={localFilter.schema}
                onChange={(e) => setLocalFilter({ ...localFilter, schema: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Schemas</option>
                {availableSchemas.map((sch) => (
                  <option key={sch} value={sch}>
                    Schema: {sch}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Display Mode Switch (Dim vs Hide) */}
          <div className="p-3.5 bg-gray-50 dark:bg-gray-750 border border-gray-200 dark:border-gray-700 rounded-lg">
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Unmatched Entities Canvas Visibility:
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setLocalFilter({ ...localFilter, hideUnmatched: false })}
                className={`px-3 py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-2 border transition-colors ${
                  !localFilter.hideUnmatched
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-500'
                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-100'
                }`}
              >
                <Eye size={14} />
                <span>Dim Unmatched (18%)</span>
              </button>
              <button
                type="button"
                onClick={() => setLocalFilter({ ...localFilter, hideUnmatched: true })}
                className={`px-3 py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-2 border transition-colors ${
                  localFilter.hideUnmatched
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-500'
                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-100'
                }`}
              >
                <EyeOff size={14} />
                <span>Hide Unmatched Completely</span>
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleReset}
                className="px-3 py-1.5 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex items-center gap-1 font-medium"
              >
                <RotateCcw size={12} />
                <span>Reset Filters</span>
              </button>
            )}
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Showing <strong>{matchingNodes.length}</strong> of <strong>{nodes.length}</strong> entities
            </span>
          </div>

          <div className="flex items-center gap-2">
            {matchingNodes.length > 0 && matchingNodes.length < nodes.length && (
              <button
                type="button"
                onClick={handleSelectMatchingOnCanvas}
                className="px-3 py-2 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                title="Select matching nodes on canvas for batch moving/editing"
              >
                Select Matching Nodes
              </button>
            )}
            <button
              type="button"
              onClick={handleApply}
              className="px-4 py-2 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Apply Filter
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterEntitiesModal;
